import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Psychology,
  Add,
  TrendingUp,
  Restaurant
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';

const FoodPrediction = () => {
  // State for form inputs
  const [guests, setGuests] = useState('');
  const [locationId, setLocationId] = useState('');
  const [menuId, setMenuId] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [response, setResponse] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Backend data
  const [menus, setMenus] = useState([]);
  const [locations, setLocations] = useState([]);
  const [currentMenuItems, setCurrentMenuItems] = useState([]);

  // Load data from backend
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load menus
        const menusResponse = await axios.get('https://stu150255.webhosting.arden.ac.uk/COM6008/api/menus/');
        setMenus(menusResponse.data.menus || []);

        // Load locations  
        const locationsResponse = await axios.get('https://stu150255.webhosting.arden.ac.uk/COM6008/api/locations/');
        setLocations(locationsResponse.data.locations || []);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load menus and locations');
      }
    };

    loadData();
  }, []);

  // Handler for menu selection
  const handleMenuChange = (selectedMenuId) => {
    setMenuId(selectedMenuId);
    const selectedMenu = menus.find(menu => menu.id === selectedMenuId);
    if (selectedMenu) {
      setCurrentMenuItems(selectedMenu.items || []);
      setSelectedItems([]); // Reset selected items when menu changes
    }
  };

  // Handler for selecting/deselecting menu items
  const handleItemChange = (itemName) => {
    if (selectedItems.includes(itemName)) {
      setSelectedItems(selectedItems.filter(i => i !== itemName));
    } else {
      setSelectedItems([...selectedItems, itemName]);
    }
  };

  // Handler for prediction
  const handlePrediction = async () => {
    if (!guests || !locationId || !menuId || selectedItems.length === 0) {
      setError('Please fill in all fields and select menu items');
      return;
    }

    if (parseInt(guests) <= 0) {
      setError('Number of guests must be greater than 0');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post('https://stu150255.webhosting.arden.ac.uk/COM6008/api/predict/', {
        guests: parseInt(guests),
        items: selectedItems,
        menu_id: parseInt(menuId),
        location_id: parseInt(locationId),
      });

      console.log('Prediction response:', response.data);
      setResponse(response.data);
      
      const predictionData = {
        guests: parseInt(guests),
        locationId: parseInt(locationId),
        menuId: parseInt(menuId),
        selectedItems,
        timestamp: new Date(),
      };
      setPrediction(predictionData);
      
      // Show success message
      setError('');
      alert('Prediction saved and can be accessed from Cost Calculation Panel');

    } catch (err) {
      console.error('Prediction error:', err);
      setError(err.response?.data?.error || 'Failed to generate prediction');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setGuests('');
    setLocationId('');
    setMenuId('');
    setSelectedItems([]);
    setCurrentMenuItems([]);
    setResponse(null);
    setPrediction(null);
    setError('');
  };

  return (
    <Box sx={{ p: 3 }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #667eea, #764ba2)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 4
          }}
        >
          <Psychology sx={{ mr: 2, fontSize: 'inherit', color: '#667eea' }} />
          AI Food Consumption Prediction
        </Typography>
      </motion.div>

      <Grid container spacing={3}>
        {/* Prediction Form */}
        <Grid item xs={12} lg={6}>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <Card sx={{ height: 'fit-content' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Event Details
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                {/* Number of Guests Input */}
                <TextField
                  fullWidth
                  type="number"
                  label="Number of Guests"
                  value={guests}
                  onChange={(e) => {
                    const guestCount = parseInt(e.target.value);
                    if (guestCount < 0) return;
                    setGuests(guestCount.toString());
                  }}
                  sx={{ mb: 3 }}
                  InputProps={{ inputProps: { min: 0 } }}
                />

                {/* Location Selection */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Select Guest Location</InputLabel>
                  <Select
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                    label="Select Guest Location"
                  >
                    {locations.map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Menu Selection */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Select Menu</InputLabel>
                  <Select
                    value={menuId}
                    onChange={(e) => handleMenuChange(e.target.value)}
                    label="Select Menu"
                  >
                    {menus.map((menu) => (
                      <MenuItem key={menu.id} value={menu.id}>
                        {menu.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Menu Items Selection */}
                {menuId && currentMenuItems.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Menu Items
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      {currentMenuItems.map((item, index) => (
                        <FormControlLabel
                          key={index}
                          control={
                            <Checkbox
                              checked={selectedItems.includes(item.name)}
                              onChange={() => handleItemChange(item.name)}
                            />
                          }
                          label={`${item.name} (${item.unit}) - PKR ${item.base_price}`}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Selected Items Display */}
                {selectedItems.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Selected Items
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedItems.map((item, index) => (
                        <Chip
                          key={index}
                          label={item}
                          onDelete={() => handleItemChange(item)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handlePrediction}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <Psychology />}
                    sx={{
                      background: 'linear-gradient(45deg, #667eea, #764ba2)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #5a6fd8, #6a4190)',
                      }
                    }}
                  >
                    {loading ? 'Predicting...' : 'Predict Consumption'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    sx={{ minWidth: '120px' }}
                  >
                    Reset
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Results Section */}
        <Grid item xs={12} lg={6}>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            {prediction && response && (
              <Card>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    <TrendingUp sx={{ mr: 1, color: '#667eea' }} />
                    Prediction Results
                  </Typography>

                  {/* Prediction Summary */}
                  <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa' }}>
                    <Typography variant="h6" gutterBottom>
                      Event Summary
                    </Typography>
                    <Typography><strong>Guests:</strong> {prediction.guests}</Typography>
                    <Typography><strong>Location:</strong> {locations.find(loc => loc.id === prediction.locationId)?.name || 'Unknown'}</Typography>
                    <Typography><strong>Menu:</strong> {menus.find(menu => menu.id === prediction.menuId)?.name || 'Unknown'}</Typography>
                    <Typography><strong>Items:</strong> {prediction.selectedItems.join(', ')}</Typography>
                    <Typography><strong>Predicted On:</strong> {prediction.timestamp.toLocaleString()}</Typography>
                  </Paper>

                  {/* Prediction Results Table */}
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Item</strong></TableCell>
                          <TableCell align="right"><strong>Predicted Amount</strong></TableCell>
                          <TableCell align="right"><strong>Unit</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(response).map(([item, amount]) => (
                          <TableRow key={item}>
                            <TableCell>{item.replace('_', ' ')}</TableCell>
                            <TableCell align="right">{amount}</TableCell>
                            <TableCell align="right">
                              {item === 'Naan' || item === 'Ice_Cream' || item === 'Salad' ? 'units' : 'kg'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Alert severity="success" sx={{ mt: 2 }}>
                    Prediction saved successfully! You can access it from the Cost Calculation panel.
                  </Alert>
                </CardContent>
              </Card>
            )}

            {!prediction && (
              <Card>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Restaurant sx={{ fontSize: 80, color: '#e0e0e0', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Fill in the form to generate food consumption predictions
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Our AI will analyze guest patterns and predict the optimal food quantities needed
                  </Typography>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FoodPrediction; 